import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Avatar,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Divider,
  styled,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  AppBar,
  Toolbar,
  Switch,
  Drawer,
  Chip,
  Button,
  LinearProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  EmojiEmotions as EmojiIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  VideoCall as VideoCallIcon,
  PushPin as PinIcon,
  Translate as TranslateIcon,
  DarkMode as DarkModeIcon,
  Mic as MicIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

const Header = styled(AppBar)({
  backgroundColor: '#000000',
  borderBottom: '2px solid #ffd700',
});

const Footer = styled(Box)({
  backgroundColor: '#000000',
  borderTop: '2px solid #ffd700',
  padding: '1rem',
  textAlign: 'center',
  color: '#ffd700',
});

const ChatContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  backgroundColor: '#1a1a1a',
  margin: '20px 0',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid #ffd700',
  boxShadow: '0 8px 32px rgba(255,215,0,0.1)',
}));

const ChatSidebar = styled(Box)(({ theme }) => ({
  width: '320px',
  backgroundColor: '#000000',
  borderRight: '1px solid #ffd700',
  display: 'flex',
  flexDirection: 'column',
}));

const ChatMain = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#1a1a1a',
});

const MessageContainer = styled(Box)({
  flex: 1,
  overflow: 'auto',
  padding: '20px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#2d2d2d',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#ffd700',
    borderRadius: '4px',
  },
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ffd700',
    color: '#ffd700',
    boxShadow: `0 0 0 2px #000000`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '1px solid currentColor',
      content: '""',
    },
  },
}));

const MessageBubble = styled(Box)(({ isUser }) => ({
  maxWidth: '70%',
  padding: '12px 16px',
  borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
  backgroundColor: isUser ? '#ffd700' : '#2d2d2d',
  color: isUser ? '#000000' : '#ffffff',
  position: 'relative',
  '&:hover': {
    '& .message-actions': {
      opacity: 1,
    },
  },
}));

const Messenger = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([
    {
      id: 1,
      name: 'Technical Support',
      lastMessage: 'Thank you for contacting us',
      unread: 2,
      online: true,
      isPinned: true,
      lastSeen: new Date(),
      typing: false,
    },
    { id: 2, name: 'Billing Support', lastMessage: 'Your invoice has been processed', unread: 1, online: false },
    { id: 3, name: 'Domain Services', lastMessage: 'Domain transfer completed', unread: 0, online: true },
  ]);
  const messageEndRef = useRef(null);
  const [isStarred, setIsStarred] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [pinnedChats, setPinnedChats] = useState([]);
  const [showTranslation, setShowTranslation] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [callStatus, setCallStatus] = useState(null);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStarChat = () => {
    setIsStarred(!isStarred);
  };

  const handleDeleteMessage = (messageId) => {
    setMessages(messages.filter(msg => msg.id !== messageId));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() && !selectedFile) return;

    setIsLoading(true);
    try {
      const newMessage = {
        id: Date.now(),
        content: message,
        sender: 'user',
        timestamp: new Date(),
        file: selectedFile,
        reactions: [],
      };

      setMessages(prev => [...prev, newMessage]);
      setMessage('');
      setSelectedFile(null);
      
      setIsTyping(true);
      setTimeout(() => {
        const aiResponse = {
          id: Date.now() + 1,
          content: 'Thank you for your message. How else can I help?',
          sender: 'agent',
          timestamp: new Date(),
          suggestions: ['View Documentation', 'Contact Support', 'Report Issue'],
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 1500);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setMessages([
      {
        id: 1,
        content: 'Hello! How can I help you today?',
        sender: 'agent',
        timestamp: new Date(Date.now() - 100000),
      },
      {
        id: 2,
        content: 'I have a question about my account',
        sender: 'user',
        timestamp: new Date(Date.now() - 80000),
      },
    ]);
  };

  const filteredChats = chats.filter(chat => 
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderHeader = () => (
    <Header position="static">
      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1, color: '#ffd700' }}>
          Messenger
        </Typography>
        <Tooltip title="Toggle Dark Mode">
          <IconButton color="inherit" onClick={() => setDarkMode(!darkMode)}>
            <DarkModeIcon sx={{ color: '#ffd700' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Settings">
          <IconButton color="inherit">
            <SettingsIcon sx={{ color: '#ffd700' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Help">
          <IconButton color="inherit">
            <HelpIcon sx={{ color: '#ffd700' }} />
          </IconButton>
        </Tooltip>
      </Toolbar>
      {isLoading && <LinearProgress sx={{ backgroundColor: '#ffd700' }} />}
    </Header>
  );

  const renderFooter = () => (
    <Footer>
      <Typography variant="body2">
        © 2024 Messenger App • All rights reserved
      </Typography>
    </Footer>
  );

  return (
    <Box>
      {renderHeader()}
      <Container maxWidth="xl">
        <ChatContainer>
          <ChatSidebar>
            <Box sx={{ p: 2, backgroundColor: '#000000' }}>
              <TextField
                fullWidth
                placeholder="Search messages..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#ffd700' }} />,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderColor: '#ffd700',
                    color: '#ffffff',
                  },
                }}
              />
              {pinnedChats.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffd700', mb: 1 }}>
                    Pinned Chats
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider sx={{ borderColor: '#ffd700' }} />
            <List sx={{ flex: 1, overflow: 'auto', bgcolor: '#000000' }}>
              {filteredChats.map((chat) => (
                <ListItem
                  key={chat.id}
                  button
                  selected={selectedChat?.id === chat.id}
                  onClick={() => handleChatSelect(chat)}
                  sx={{
                    '&:hover': { backgroundColor: '#2d2d2d' },
                    '&.Mui-selected': { backgroundColor: '#3d3d3d' },
                  }}
                >
                  <ListItemAvatar>
                    <StyledBadge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      invisible={!chat.online}
                    >
                      <Avatar sx={{ bgcolor: '#ffd700', color: '#000000' }}>
                        {chat.name[0]}
                      </Avatar>
                    </StyledBadge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography color="#ffffff">
                        {chat.name}
                        {chat.isPinned && (
                          <PinIcon sx={{ ml: 1, fontSize: 16, color: '#ffd700' }} />
                        )}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="#888888" noWrap>
                        {chat.typing ? 'Typing...' : chat.lastMessage}
                      </Typography>
                    }
                  />
                  {chat.unread > 0 && (
                    <Chip
                      size="small"
                      label={chat.unread}
                      sx={{
                        bgcolor: '#ffd700',
                        color: '#000000',
                        ml: 1,
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </ChatSidebar>

          <ChatMain>
            {selectedChat ? (
              <>
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid #ffd700',
                  backgroundColor: '#000000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StyledBadge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      invisible={!selectedChat.online}
                    >
                      <Avatar sx={{ bgcolor: '#ffd700', color: '#000000' }}>
                        {selectedChat.name[0]}
                      </Avatar>
                    </StyledBadge>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#ffffff' }}>
                        {selectedChat.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888888' }}>
                        {selectedChat.online ? 'Online' : 'Offline'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Voice call">
                      <IconButton sx={{ color: '#ffd700' }}>
                        <PhoneIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Video call">
                      <IconButton sx={{ color: '#ffd700' }}>
                        <VideoCallIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isStarred ? 'Unstar chat' : 'Star chat'}>
                      <IconButton onClick={handleStarChat} sx={{ color: '#ffd700' }}>
                        {isStarred ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    <IconButton 
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      sx={{ color: '#ffd700' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                <MessageContainer>
                  {messages.map((msg) => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      {msg.sender === 'agent' && (
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1,
                            bgcolor: '#ffd700',
                            color: '#000000',
                          }}
                        >
                          {selectedChat.name[0]}
                        </Avatar>
                      )}
                      <MessageBubble isUser={msg.sender === 'user'}>
                        <Typography>{msg.content}</Typography>
                        <Box sx={{ 
                          mt: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: 0.7,
                        }}>
                          <Typography variant="caption">
                            {msg.timestamp.toLocaleTimeString()}
                          </Typography>
                          <Box 
                            className="message-actions" 
                            sx={{ 
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              display: 'flex',
                              gap: 1,
                            }}
                          >
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteMessage(msg.id)}
                              sx={{ color: msg.sender === 'user' ? '#000000' : '#ffffff' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </MessageBubble>
                    </Box>
                  ))}
                  {isTyping && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#ffd700', color: '#000000' }}>
                        {selectedChat.name[0]}
                      </Avatar>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#2d2d2d', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <CircularProgress size={16} sx={{ color: '#ffd700' }} />
                        <Typography sx={{ color: '#ffffff' }}>Typing...</Typography>
                      </Box>
                    </Box>
                  )}
                  <div ref={messageEndRef} />
                </MessageContainer>

                <Box sx={{ 
                  p: 2, 
                  borderTop: '1px solid #ffd700',
                  backgroundColor: '#000000',
                }}>
                  <form onSubmit={handleSendMessage}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Add emoji">
                        <IconButton size="small" sx={{ color: '#ffd700' }}>
                          <EmojiIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Attach file">
                        <IconButton size="small" component="label" sx={{ color: '#ffd700' }}>
                          <input type="file" hidden />
                          <AttachFileIcon />
                        </IconButton>
                      </Tooltip>
                      <TextField
                        fullWidth
                        size="small"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type a message..."
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            backgroundColor: '#2d2d2d',
                            color: '#ffffff',
                            '& fieldset': { borderColor: '#ffd700' },
                            '&:hover fieldset': { borderColor: '#ffed4a' },
                            '& input::placeholder': { color: '#888888' },
                          },
                        }}
                      />
                      <Tooltip title="Send message">
                        <IconButton 
                          type="submit" 
                          sx={{
                            backgroundColor: '#ffd700',
                            color: '#000000',
                            '&:hover': { backgroundColor: '#ffed4a' },
                            '&.Mui-disabled': {
                              backgroundColor: '#2d2d2d',
                              color: '#666666',
                            },
                          }}
                          disabled={!message.trim() || isTyping}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </form>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                flexDirection: 'column',
                gap: 2,
                color: '#ffffff',
              }}>
                <Typography variant="h6">
                  Select a chat to start messaging
                </Typography>
                <Typography variant="body2" sx={{ color: '#888888' }}>
                  Choose from your existing conversations
                </Typography>
              </Box>
            )}
          </ChatMain>
        </ChatContainer>
      </Container>
      {renderFooter()}
      
      <Snackbar
        open={notifications.length > 0}
        autoHideDuration={6000}
        onClose={() => setNotifications([])}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {notifications[0]}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Messenger; 